import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { REVIEW_SORTS, REVIEW_TABS, TASK_TYPES, type ReviewTab, type TaskType } from '@mybusinessmate/schema';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { PageHeader } from '../../../../../../components/shell/page-header';
import { ReviewRow, ReviewRowSkeleton, ReviewRowsHeader } from '../../../../../../components/reviews/review-row';
import { EmptyState } from '../../../../../../components/ui/empty-state';
import { ErrorState, Forbidden, NoResults } from '../../../../../../components/ui/state-views';
import { SegmentedControl } from '../../../../../../components/ui/segmented-control';
import { FilterSelectPill, ToolbarSearch } from '../../../../../../components/ui/toolbar-controls';
import { reviewsQuery } from '../../../../../../lib/tasks-api';
import { TASK_TYPE_META } from '../../../../../../lib/task-display';
import { useDebouncedValue } from '../../../../../../lib/use-debounced-value';

type Sort = (typeof REVIEW_SORTS)[number];

const TAB_LABEL: Record<ReviewTab, string> = {
  waiting: 'Waiting for me',
  fixes: 'Draft fixes',
  sent_back: 'Sent back',
};
const SORT_LABEL: Record<Sort, string> = { client_date: 'Client date', waiting: 'Waiting longest' };
const EMPTY = (pod: string | undefined): Record<ReviewTab, { title: string; description: string }> => ({
  waiting: {
    title: 'All caught up',
    description: `No drafts are waiting for your QA. New ones from ${pod ? `the ${pod}` : 'your team'} will show up here, oldest client date first.`,
  },
  fixes: { title: 'No draft fixes', description: 'Small fixes to what the client already has show up here.' },
  sent_back: { title: 'Nothing sent back', description: 'Drafts you ask changes on wait here for their author.' },
});

export const Route = createFileRoute('/subdomain/admin/_staff/workbench/reviews/')({
  // Filters live in the URL so a reload or a shared link shows the same queue.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: ReviewTab; q?: string; type?: TaskType; sort?: Sort; pod?: string } => ({
    ...((REVIEW_TABS as readonly unknown[]).includes(search.tab) ? { tab: search.tab as ReviewTab } : {}),
    ...(typeof search.q === 'string' && search.q ? { q: search.q } : {}),
    ...((TASK_TYPES as readonly unknown[]).includes(search.type) ? { type: search.type as TaskType } : {}),
    ...((REVIEW_SORTS as readonly unknown[]).includes(search.sort) ? { sort: search.sort as Sort } : {}),
    ...(typeof search.pod === 'string' && search.pod ? { pod: search.pod } : {}),
  }),
  staticData: { wide: true },
  component: ReviewQueuePage,
});

/** The lead's review queue (Figma A05.5): drafts their team has sent to QA, oldest client date first. Nothing here reaches the client. */
function ReviewQueuePage() {
  const { tab = 'waiting', q, type, sort = 'client_date', pod } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [text, setText] = useState(q ?? '');
  const search = useDebouncedValue(text.trim(), 250) || undefined;
  if (search !== q) void navigate({ search: (prev) => ({ ...prev, q: search }), replace: true });

  const { data, isLoading, error, refetch } = useQuery({
    ...reviewsQuery({ tab, q: search, type, sort, pod }),
    retry: false,
  });
  const kind = (error as Partial<MappedApiError> | null)?.kind;
  const set = (patch: Record<string, unknown>) => void navigate({ search: (prev) => ({ ...prev, ...patch }) });

  const counts = data?.counts;
  const countOf: Record<ReviewTab, number | undefined> = {
    waiting: counts?.waiting,
    fixes: counts?.fixes,
    sent_back: counts?.sentBack,
  };

  return (
    <>
      <PageHeader
        title="Review queue"
        description={`Drafts from ${pod ? `the ${pod}` : 'your team'} waiting for your QA. Clients can’t see anything on this page.`}
        actions={
          <FilterSelectPill
            label="Pod"
            size="lg"
            bare
            allText="All pods"
            value={pod ?? ''}
            onChange={(value) => set({ pod: value || undefined })}
            options={(data?.pods ?? []).map((p) => ({ value: p, label: p }))}
          />
        }
      />

      <div className="flex flex-wrap items-center gap-(--mbm-space-2-5)">
        <ToolbarSearch
          filled
          size="lg"
          aria-label="Search the review queue"
          placeholder="Search tasks or clients"
          value={text}
          onChange={setText}
          className="w-full sm:w-(--mbm-layout-toolbar-search-mid-width)"
        />
        <SegmentedControl
          shape="pill"
          size="lg"
          aria-label="Queue"
          options={REVIEW_TABS.map((value) => ({ value, label: TAB_LABEL[value], count: countOf[value] }))}
          value={tab}
          onChange={(next) => set({ tab: next })}
        />
        <span className="ml-auto flex flex-wrap items-center gap-(--mbm-space-2-5)">
          <FilterSelectPill
            label="Type"
            size="lg"
            showAll
            value={type ?? ''}
            onChange={(value) => set({ type: value || undefined })}
            options={TASK_TYPES.map((t) => ({ value: t, label: TASK_TYPE_META[t].label }))}
          />
          <FilterSelectPill
            label="Sort"
            size="lg"
            showAll
            allText={SORT_LABEL.client_date}
            value={sort === 'client_date' ? '' : sort}
            onChange={(value) => set({ sort: value || undefined })}
            options={[{ value: 'waiting', label: SORT_LABEL.waiting }]}
          />
        </span>
      </div>

      <section className="overflow-hidden rounded-(--mbm-component-radius-card) bg-surface-card shadow-(--mbm-shadow-table-card)">
        {kind === 'forbidden' ? (
          <Forbidden
            title="The review queue is for leads"
            description="Specialists send drafts to it. A lead, your account manager or an admin answers them."
          />
        ) : error ? (
          <ErrorState title="Couldn’t load the queue" onRetry={() => void refetch()} />
        ) : (
          <>
            <ReviewRowsHeader />
            {isLoading ? (
              Array.from({ length: 4 }, (_, i) => <ReviewRowSkeleton key={i} />)
            ) : data && data.items.length > 0 ? (
              data.items.map((item) => <ReviewRow key={item.submissionId} item={item} />)
            ) : search || type ? (
              <NoResults
                query={search}
                description="Try fewer words, or clear the filters."
                onClear={() => {
                  setText('');
                  void navigate({ search: (prev) => ({ tab: prev.tab, pod: prev.pod }) });
                }}
              />
            ) : (
              <EmptyState {...EMPTY(pod)[tab]} />
            )}
            {data && data.items.length > 0 ? (
              <p className="px-(--mbm-space-6) py-(--mbm-space-4) text-body-xsmall text-text-tertiary">
                {sort === 'waiting' ? 'Longest waiting first.' : 'Oldest first by client date.'}
              </p>
            ) : null}
          </>
        )}
      </section>
    </>
  );
}
